import React from "react";
import { motion } from "framer-motion";
import { Check, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import BrandStripe from "./BrandStripe";

const traditional = ["Generic", "Difficult to shop", "Clinical", "Disposable"];
const goodEyes = ["Design-led", "Colour coded", "Sustainable", "Fashion forward", "Easy to navigate", "Pharmacy friendly"];

export const NewKindOfReader = ({ compact = false }) => {
  return (
    <section
      id="difference"
      data-testid="difference-section"
      className="relative bg-white py-24 md:py-32 border-t border-[color:var(--ge-navy)]/10 overflow-hidden"
    >
      <div className="ge-container">
        <SectionHeader
          kicker="Our Difference"
          title={<>A new kind<br />of reader.</>}
          subtitle="Reading glasses used to feel clinical and hard to shop. Good Eyes reimagines the category: design-led, colour-coded, sustainable and easy to navigate."
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55 }}
            className="rounded-2xl border-2 border-[color:var(--ge-navy)]/15 bg-[color:var(--ge-cream)] p-6 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--ge-navy)]/60">
                Traditional readers
              </span>
              <span className="h-[2px] w-8 rounded-full bg-[color:var(--ge-navy)]/20" />
            </div>
            <h3 className="mt-3 tight-cap font-bold text-[color:var(--ge-navy)]/80 text-2xl md:text-3xl">
              What&apos;s already out there
            </h3>
            <ul className="mt-8 space-y-4 flex-1">
              {traditional.map((t) => (
                <li key={t} className="flex items-center gap-4">
                  <span className="h-9 w-9 rounded-full inline-flex items-center justify-center border-2 border-[color:var(--ge-navy)]/25 text-[color:var(--ge-navy)]/50">
                    <X size={18} strokeWidth={2.5} />
                  </span>
                  <span className="text-lg md:text-xl font-medium text-[color:var(--ge-navy)]/70 line-through decoration-[color:var(--ge-navy)]/25">
                    {t}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="rounded-2xl p-6 md:p-10 flex flex-col text-white"
            style={{ background: "var(--ge-navy)" }}
          >
            <div className="flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold" style={{ color: "var(--ge-yellow)" }}>
                Good Eyes
              </span>
              <span className="h-[2px] w-8 rounded-full" style={{ background: "var(--ge-yellow)" }} />
            </div>
            <h3 className="mt-3 tight-cap font-bold text-white text-2xl md:text-3xl">
              What we do differently
            </h3>
            <ul className="mt-8 space-y-4 flex-1">
              {goodEyes.map((t) => (
                <li key={t} className="flex items-center gap-4">
                  <span className="h-9 w-9 rounded-full inline-flex items-center justify-center text-[color:var(--ge-navy)]" style={{ background: "var(--ge-yellow)" }}>
                    <Check size={18} strokeWidth={3} />
                  </span>
                  <span className="text-lg md:text-xl font-semibold text-white">{t}</span>
                </li>
              ))}
            </ul>
            {!compact && (
              <Link
                to="/shop"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[color:var(--ge-navy)] px-5 py-3 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-yellow)] transition-colors self-start"
              >
                Shop the range <ArrowRight size={14} />
              </Link>
            )}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={22} />
      </div>
    </section>
  );
};

export default NewKindOfReader;
